import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from '@loop-coffee/commerce-contracts';

import { PixelCoin } from './cartoon/Art';
import { buyLink } from './cartoon/links';
import { useCatalog, useCommerce } from '../commerce/CommerceContext';
import { isListed, toBlendContent } from '../commerce/catalog';

// Hoje a Loop tem um único café: o BitBean.
const ficha = [
    ['Variedade', 'Catuaí'],
    ['Processo', 'Natural'],
    ['Altitude', '1.050 m'],
    ['Torra', 'Média'],
    ['Pontuação', '82 pontos'],
    ['Pacote', '250 g'],
];

const notas = ['chocolate amargo', 'amêndoas', 'caramelo'];

// Checkout disabled (feature flag off): editorial showcase with the WhatsApp CTA, no Store API call.
function ShowcaseBlends() {
    return (
        <section className="lc lc-section bg-violeta" id="bitbean">
            <div className="lc-wrap lc-grid-2">
                <div style={{ position: 'relative', maxWidth: 520, width: '100%', margin: '0 auto' }}>
                    <div className="lc-card" style={{ padding: 20 }}>
                        <div style={{ background: '#000', borderRadius: 16, border: '4px solid #000', padding: 24 }}>
                            <img
                                src="/bitbean-label-250g.png"
                                alt="Pacote de 250 g do café BitBean"
                                style={{ width: '100%', height: 'auto', display: 'block' }}
                            />
                        </div>
                    </div>
                    <div className="lc-level" style={{ position: 'absolute', top: -22, right: -12, transform: 'rotate(4deg)' }}>
                        <strong>LEVEL UP!</strong>
                        <span>82 pontos</span>
                    </div>
                </div>

                <div>
                    <span className="lc-pill">conheça o BitBean</span>
                    <h2 className="lc-title">Do Cerrado Mineiro pra sua xícara<span className="lc-dot">.</span></h2>
                    <p className="lc-text">
                        Cultivado pelo Antônio Pires, na Fazenda Pântano, em Patos de Minas (MG). É 100% arábica
                        e passa pelo processo natural: o grão seca dentro do fruto, e isso deixa o café mais docinho.
                    </p>

                    <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '14px 24px', margin: '28px 0 0', maxWidth: 520 }}>
                        {ficha.map(([nome, valor]) => (
                            <div key={nome} style={{ borderBottom: '2px solid var(--lc-lil)', paddingBottom: 8 }}>
                                <dt style={{ fontSize: 14, fontWeight: 500, opacity: .85 }}>{nome}</dt>
                                <dd style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{valor}</dd>
                            </div>
                        ))}
                    </dl>

                    <div className="lc-chips">
                        {notas.map((n) => <span key={n} className="lc-chip">{n}</span>)}
                    </div>

                    <div className="lc-actions">
                        <a href={buyLink} target="_blank" rel="noopener noreferrer" className="lc-btn lc-btn-coin">
                            <PixelCoin /> Comprar pelo WhatsApp
                        </a>
                        <a href="#preparo" className="lc-btn-ghost">Como preparar</a>
                    </div>
                    <p style={{ fontSize: 15, marginTop: 18, opacity: .9 }}>
                        Por enquanto, os pedidos são feitos pelo WhatsApp. Em breve, direto aqui no site.
                    </p>
                </div>
            </div>
        </section>
    );
}

const isPurchasable = (variant) =>
    Boolean(variant) && (!variant.manage_inventory || variant.allow_backorder || variant.inventory_quantity > 0);

const optionValue = (variant, optionId) => variant.options?.find((o) => o.option_id === optionId)?.value;

// Options with a single value need no choice; the card stays as it was for one-variant products.
const choosableOptions = (product) => (product.options || []).filter((option) => (option.values || []).length > 1);

function initialChoice(product) {
    const variants = product.variants || [];
    const variant = variants.find(isPurchasable) || variants[0];
    return Object.fromEntries((product.options || []).map((option) => [option.id, variant && optionValue(variant, option.id)]));
}

const variantFor = (product, choice) =>
    (product.variants || []).find((variant) => Object.entries(choice).every(([id, value]) => optionValue(variant, id) === value));

function OptionPicker({ option, value, onChange }) {
    return (
        <div role="group" aria-label={option.title}>
            <span style={{ fontSize: 13, opacity: .75 }}>{option.title.toLowerCase()}:</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                {option.values.map(({ value: v }) => {
                    const selected = v === value;
                    return (
                        <button
                            key={v}
                            type="button"
                            aria-pressed={selected}
                            onClick={() => onChange(v)}
                            style={{
                                borderRadius: 999, border: '2px solid #000', padding: '6px 16px', fontSize: 15, fontWeight: 600, cursor: 'pointer',
                                background: selected ? '#000' : '#fff', color: selected ? '#fff' : '#000',
                            }}
                        >
                            {v}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// Presentational only: `price`, `badge`, `options` and `action` are supplied by the catalog source.
function BlendCard({ content, badge, price, options, action }) {
    return (
        <article className="lc-card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
            <div style={{ position: 'relative', background: content.imageBackground, borderRadius: 16, border: '4px solid #000', padding: 16 }}>
                {content.image && (
                    <img src={content.image} alt={content.name} style={{ width: '100%', height: 'auto', display: 'block' }} />
                )}
                <span className="lc-chip" style={{ position: 'absolute', top: 12, right: 12, color: '#fff', fontSize: 13 }}>{badge}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                    <code style={{ fontSize: 13, color: content.color }}>blend.{content.slug}</code>
                    <h3 style={{ fontSize: 26, fontWeight: 700, margin: '4px 0 0' }}>{content.name}</h3>
                </div>
                {price && <span data-testid="blend-price" style={{ fontSize: 22, fontWeight: 700, whiteSpace: 'nowrap' }}>{price}</span>}
            </div>
            {content.tagline && <p style={{ fontSize: 16 }}>{content.tagline}</p>}
            {content.specs.length > 0 && (
                <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 20px', margin: 0 }}>
                    {content.specs.map((spec) => (
                        <div key={spec.key} style={{ borderBottom: '2px solid var(--lc-lil)', paddingBottom: 6 }}>
                            <dt style={{ fontSize: 13, fontWeight: 500, opacity: .75 }}>{spec.label}:</dt>
                            <dd style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>{spec.value}</dd>
                        </div>
                    ))}
                    {content.score && (
                        <div style={{ borderBottom: '2px solid var(--lc-lil)', paddingBottom: 6 }}>
                            <dt style={{ fontSize: 13, fontWeight: 500, opacity: .75 }}>pontuação:</dt>
                            <dd style={{ fontSize: 17, fontWeight: 700, margin: 0, color: content.color }}>{content.score}</dd>
                        </div>
                    )}
                </dl>
            )}
            {content.notes.length > 0 && (
                <div>
                    <span style={{ fontSize: 13, opacity: .75 }}>notas:</span>
                    <div className="lc-chips" style={{ marginTop: 8 }}>
                        {content.notes.map((note, i) => <span key={i} className="lc-chip" style={{ color: '#fff' }}>{note}</span>)}
                    </div>
                </div>
            )}
            {options}
            <div style={{ marginTop: 'auto' }}>{action}</div>
        </article>
    );
}

function StatusMessage({ role = 'status', children }) {
    return (
        <div role={role} className="lc-text" style={{ margin: '32px auto 0', textAlign: 'center' }}>{children}</div>
    );
}

function StoreProduct({ product, adding, pending, onBuy }) {
    const [choice, setChoice] = React.useState(() => initialChoice(product));
    const content = toBlendContent(product);
    const options = choosableOptions(product);
    const variant = variantFor(product, choice);
    const price = variant?.calculated_price;
    const available = isPurchasable(variant);
    const label = options.length > 0 ? `${content.name} ${options.map((option) => choice[option.id]).join(' / ')}` : content.name;
    return (
        <BlendCard
            content={content}
            badge={!variant ? 'INDISPONÍVEL' : available ? 'DISPONÍVEL' : 'ESGOTADO'}
            price={price ? formatMoney(price.calculated_amount, price.currency_code) : null}
            options={options.length > 0 && (
                <div style={{ display: 'grid', gap: 14 }}>
                    {options.map((option) => (
                        <OptionPicker
                            key={option.id}
                            option={option}
                            value={choice[option.id]}
                            onChange={(value) => setChoice((current) => ({ ...current, [option.id]: value }))}
                        />
                    ))}
                </div>
            )}
            action={(
                <button
                    type="button"
                    className="lc-btn lc-btn-coin"
                    style={{ background: content.color, color: '#fff', width: '100%', justifyContent: 'center' }}
                    disabled={!available || pending}
                    onClick={() => onBuy(variant)}
                    aria-label={!variant ? `${label} indisponível` : available ? `Adicionar ${label} ao carrinho` : `${label} esgotado`}
                >
                    {available && <PixelCoin />}
                    {!variant ? 'Indisponível' : !available ? 'Esgotado' : adding === variant.id ? 'Adicionando…' : 'Comprar'}
                </button>
            )}
        />
    );
}

function StoreBlends() {
    const catalog = useCatalog();
    const { addItem, pending, error, dismissError } = useCommerce();
    const navigate = useNavigate();
    const [adding, setAdding] = React.useState(null);

    if (catalog.status === 'loading') {
        return <StatusMessage>Carregando cafés…</StatusMessage>;
    }

    if (catalog.status === 'error') {
        return (
            <StatusMessage role="alert">
                Não foi possível carregar os cafés agora.
                <span className="lc-actions" style={{ justifyContent: 'center' }}>
                    <button type="button" className="lc-btn-ghost" onClick={catalog.retry}>Tentar novamente</button>
                    <a href={buyLink} target="_blank" rel="noopener noreferrer" className="lc-btn">Pedir pelo WhatsApp</a>
                </span>
            </StatusMessage>
        );
    }

    const products = catalog.products.filter(isListed);

    if (catalog.status === 'empty' || products.length === 0) {
        return <StatusMessage>Nenhum café disponível no momento. Volte em breve.</StatusMessage>;
    }

    const buy = async (variant) => {
        setAdding(variant.id);
        const cart = await addItem(variant.id, 1);
        setAdding(null);
        if (cart) navigate('/checkout');
    };

    return (
        <>
            {error && adding === null && (
                <div role="alert" className="lc-card" style={{ marginTop: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, padding: 16 }}>
                    <span>Não foi possível adicionar ao carrinho: {error.message}</span>
                    <button type="button" className="lc-btn-ghost" style={{ color: '#000' }} onClick={dismissError}>Fechar</button>
                </div>
            )}
            <div style={{ marginTop: 48, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 460px))', justifyContent: 'center', gap: 40 }}>
                {products.map((product) => (
                    <StoreProduct key={product.id} product={product} adding={adding} pending={pending} onBuy={buy} />
                ))}
            </div>
        </>
    );
}

// With the checkout enabled every card comes from the Medusa Admin through `toBlendContent`.
function CatalogBlends() {
    return (
        <section className="lc lc-section bg-violeta" id="bitbean">
            <div className="lc-wrap">
                <div className="lc-center">
                    <span className="lc-pill">nossos cafés</span>
                    <h2 className="lc-title">Escolha o seu café<span className="lc-dot">.</span></h2>
                </div>
                <StoreBlends />
            </div>
        </section>
    );
}

export default function Blends() {
    const { enabled } = useCommerce();
    return enabled ? <CatalogBlends /> : <ShowcaseBlends />;
}
